import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './hooks/useToast';
import { useWallet } from './WalletContext';
import './SingleDigitGame.css';

function SingleDigitGame({ gameName, onBack, gameType = 'single-digit' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { walletBalance, updateWalletBalance } = useWallet();
  const [digit, setDigit] = useState('');
  const [points, setPoints] = useState('');
  const [bids, setBids] = useState([]);
  const [user, setUser] = useState(null);
  const [totalBids, setTotalBids] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [showBidPlaced, setShowBidPlaced] = useState(false);
  const [marketStatus, setMarketStatus] = useState('');
  const [availableDigits, setAvailableDigits] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dropdownRef = useRef(null);

  // Get times and game data from location state
  const openTime = location.state?.openTime || '09:00';
  const closeTime = location.state?.closeTime || '18:00';
  const gameId = location.state?.gameId || '';
  const gameTypeFromState = location.state?.gameTypeId || '';
  const storedUser = localStorage.getItem('user');
  const playerId = storedUser ? JSON.parse(storedUser).id : '';

  // Convert clock time to market-day minutes where 0 = 2:00 AM reset point.
  // Keeps 12:00 AM–1:59 AM in the same market day as the preceding evening.
  const toMarketMinutes = (h, m) => ((h * 60 + m) - 120 + 1440) % 1440;

  const getKolkata = () => {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  };

  // True when we are within 5 minutes of (or past) the open time — Open bids are done
  const isOpenBidClosed = () => {
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const [h, m] = openTime.split(':').map(Number);
    return current >= toMarketMinutes(h, m) - 5;
  };

  // True when we are within 5 minutes of (or past) the close time — market is done
  const isMarketClosed = () => {
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const [h, m] = closeTime.split(':').map(Number);
    return current >= toMarketMinutes(h, m) - 5;
  };

  // Fetch Jodi digits from API
  useEffect(() => {
    const fetchDigits = async () => {
      try {
        const response = await fetch('https://backend.mainbazar777.site/api/game-ank');
        const data = await response.json();
        if (data.game_ank && data.game_ank.length > 0) {
          setAvailableDigits(data.game_ank[0]);
        }
      } catch (err) {
        console.error('Failed to fetch digits:', err);
      }
    };

    fetchDigits();
  }, []);

  // Update suggestions when digit changes
  useEffect(() => {
    if (gameType === 'jodi' && digit.trim()) {
      const jodiDigits = availableDigits.jodi_digit;
      if (jodiDigits) {
        let parsed = jodiDigits;
        if (typeof jodiDigits === 'string') {
          try {
            parsed = JSON.parse(jodiDigits);
          } catch (e) {
            console.error('Error parsing jodi digits:', e);
            setSuggestions([]);
            return;
          }
        }

        const filtered = parsed
          .filter(d => d.toString().includes(digit.trim()))
          .slice(0, 10);
        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [digit, gameType, availableDigits]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Set initial market status and auto-switch to Close when open bid time closes
  useEffect(() => {
    if (isOpenBidClosed()) {
      setMarketStatus('Close');
      return;
    }
    setMarketStatus('Open');

    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const secs = kt.getSeconds();
    const [h, m] = openTime.split(':').map(Number);
    const msUntilOpenClose = ((toMarketMinutes(h, m) - 5) - current) * 60000 - secs * 1000;

    const t = setTimeout(() => setMarketStatus('Close'), msUntilOpenClose);
    return () => clearTimeout(t);
  }, [openTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-navigate home when market closes (5 min before close time)
  useEffect(() => {
    if (isMarketClosed()) {
      navigate('/');
      return;
    }

    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const secs = kt.getSeconds();
    const [h, m] = closeTime.split(':').map(Number);
    const msUntilClose = ((toMarketMinutes(h, m) - 5) - current) * 60000 - secs * 1000;

    const t = setTimeout(() => navigate('/'), msUntilClose);
    return () => clearTimeout(t);
  }, [closeTime, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddBid = () => {
    if (!digit.trim() || !points.trim()) {
      toast.error('Please enter both digit and points');
      return;
    }

    const newBid = {
      id: Date.now(),
      pana: digit,
      point: points,
      type: 'Single'
    };

    setBids([...bids, newBid]);
    setTotalBids(totalBids + 1);
    setTotalPoints(totalPoints + parseInt(points));

    // Show bid placed notification
    setShowBidPlaced(true);
    setTimeout(() => setShowBidPlaced(false), 2000);

    // Clear inputs
    setDigit('');
    setPoints('');
  };

  const handleDeleteBid = (id) => {
    const bid = bids.find(b => b.id === id);
    setBids(bids.filter(b => b.id !== id));
    setTotalBids(totalBids - 1);
    setTotalPoints(totalPoints - parseInt(bid.point));
  };

  // Open option is disabled when we are within 5 minutes of (or past) the open time
  const isOpenDisabled = () => isOpenBidClosed();

  // Format time to 12-hour format
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${String(displayHour).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const handleSubmit = async () => {
    // Validate inputs
    if (!digit.trim() || !points.trim()) {
      toast.error('Please enter both digit and funds');
      return;
    }

    // Check minimum bid amount is 10
    const pointsValue = parseInt(points);
    if (pointsValue < 10) {
      toast.error('Minimum bid amount is 10 frs');
      return;
    }

    // Guard: market fully closed (5 min before close time)
    if (isMarketClosed()) {
      toast.error('Market Closed');
      if (navigator.vibrate) navigator.vibrate(200);
      navigate('/');
      return;
    }

    // Guard: open bid time closed (5 min before open time) — reject Open-session bids
    if (marketStatus === 'Open' && isOpenBidClosed()) {
      toast.error('Open bid time has closed. Please select Close.');
      return;
    }

    // Validate required data
    if (!playerId) {
      toast.error('User not authenticated. Please login again.');
      return;
    }

    if (!gameId) {
      toast.error('Game information not available. Please select game again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare API payload
      const payload = {
        player_id: playerId,
        session: marketStatus || null,
        bid_amount: pointsValue.toString(),
        bid_number: digit,
        game_name: gameName,
        game_id: gameId,
        game_type: gameTypeFromState
      };

      // console.log('📤 Sending API Request to play-game:');
      // console.log('Payload:', payload);

      // Call the play-game API
      const response = await fetch('https://backend.mainbazar777.site/api/play-game', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      // console.log('📥 API Response:', data);
      // console.log('Response Status:', response.status);

      if (data.balance === false) {
        toast.error('Insufficient balance. Please add funds to your wallet.');
      } else if (response.ok && data.success) {
        // Show success notification
        setShowBidPlaced(true);
        setTimeout(() => setShowBidPlaced(false), 2000);
        
        toast.success(`Bid Placed: Number ${digit}, Amount ${pointsValue} rs`);
        
        // Refresh wallet balance after successful bid
        if (playerId) {
          updateWalletBalance(playerId);
        }
        
        // Clear inputs
        setDigit('');
        setPoints('');
      } else {
        toast.error(data.message || 'Failed to place bid. Please try again.');
      }
    } catch (err) {
      console.error('API Error:', err);
      toast.error('Error placing bid. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const today = new Date();
  const dateString = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;

  return (
    <div className="single-digit-game">
      {/* Header */}
      <header className="single-digit-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="game-title">{gameName || 'SINGLE DIGIT'}</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </header>

      {/* Main Content */}
      <div className="game-content">
        {/* Game Info Section */}
        <div className="game-info-section">
          <h2 className="game-type-heading">Single Digit</h2>
          <div className="info-row">
            <div className="info-item">
              <label>Date</label>
              <input type="text" value={dateString} readOnly disabled />
            </div>
            <div className="info-item">
              <label>Status</label>
              {gameType === 'jodi' ? (
                <input type="text" value="N/A" readOnly disabled />
              ) : (
                <select
                  value={marketStatus}
                  onChange={(e) => setMarketStatus(e.target.value)}
                  className={`status-select status-${marketStatus.toLowerCase()}`}
                >
                  <option value="open" disabled={isOpenDisabled()}>Open</option>
                  <option value="close">Close</option>
                </select>
              )}
            </div>
          </div>

          <div className="time-section">
            <div className="time-item">
              <span className="time-label">Time Open:</span>
              <span className="time-value">{formatTime(openTime)}</span>
            </div>
            <div className="time-item">
              <span className="time-label">Time Close:</span>
              <span className="time-value">{formatTime(closeTime)}</span>
            </div>
          </div>
        </div>

        {/* Input Section */}
        <div className="input-section">
          <div className="input-row">
            <div className="input-item" ref={dropdownRef}>
              <label>{gameType === 'jodi' ? 'Enter Jodi:' : 'Enter Digit:'}</label>
              <div className="dropdown-container">
                <input
                  type="text"
                  placeholder={gameType === 'jodi' ? 'Jodi' : 'Single digit'}
                  value={digit}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Only allow numbers
                    value = value.replace(/[^0-9]/g, '');
                    // For jodi, limit to 2 digits
                    if (gameType === 'jodi') {
                      value = value.slice(0, 2);
                    } else {
                      value = value.slice(0, 1);
                    }
                    setDigit(value);
                    if (gameType === 'jodi') {
                      setShowSuggestions(true);
                    }
                  }}
                  onFocus={() => gameType === 'jodi' && setShowSuggestions(true)}
                  maxLength={gameType === 'jodi' ? 2 : 1}
                  className="digit-input"
                  inputMode="numeric"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="dropdown-menu">
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="dropdown-item"
                        onClick={() => {
                          setDigit(suggestion.toString());
                          setShowSuggestions(false);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="input-item">
              <label>Enter Funds: {gameType === 'jodi' && <span className="min-bet">(Min: 10)</span>}</label>
              <input
                type="number"
                placeholder={gameType === 'jodi' ? 'Min 10 frs' : 'Funds'}
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="points-input"
                min={gameType === 'jodi' ? '10' : '0'}
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button className="submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Placing Bid...' : 'Place Bid'}
        </button>
      </div>

      {/* Bid Placed Notification */}
      {showBidPlaced && (
        <div className="bid-placed-notification">
          ✓ Bid Placed Successfully
        </div>
      )}
    </div>
  );
}

export default SingleDigitGame;
